import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';
import useListActions from '@/hooks/useList/useListActions';
import usePagination from '@/hooks/usePagination';
import { useJiraIssuesQuery } from '@/modules/corporate/modules/integrations/providers/Jira/graphql/queries/generated/jiraIssues';
import useIntegrationProviderDetails from '@/modules/corporate/modules/integrations/views/IntegrationDetails/hooks/useIntegrationProviderDetails';
import { useCreateActionItemsMutation } from '@/modules/tasks/graphql/queries/generated/createActionItems';
import useFile from '@/modules/tasks/views/TasksDashboard/hooks/useFile';
import {
  ActionItemExternalEntityTypeEnum,
  CreateActionItemInput,
  IntegrationProviderEnum,
  IntegrationScopeEnum,
  JiraIssueType,
  TaskCreationTypeEnum,
} from '@/types/schema';

import { AddTaskFromJiraModalProps } from '../../components/AddTaskFromJiraModal/types';

export default function useAddTasksFromJiraModal(props: AddTaskFromJiraModalProps) {
  const { t } = useTranslation('OKRs');
  const { onClose, provider, entityId, entity, fileId, onSubmit } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch, debounceSearchStr, setSearchWithoutDebounce] = useDebounceState('');
  const [, createActionItems] = useCreateActionItemsMutation();
  const { showErrorSnackbar } = useSnackbar();
  const { isLoading, isFetching, error, fetchMore, data, isFetchingMore, totalCount } =
    usePagination(
      useJiraIssuesQuery,
      {
        filters: {
          jqlQuery: debounceSearchStr,
          cacheKey: `${entityId}-${entity}-${fileId}`,
        },
        entityFilter: entityId && entity ? { entityId, entityType: entity } : undefined,
        fileFilter: (entityId && entity) || !fileId ? undefined : { fileId },
      },
      { edgeKey: 'jiraIssues', requestPolicy: 'network-only' }, // network-only for isPreviouslySelected: isActionItemLinked handling
    );
  const { integrationProvider } = useIntegrationProviderDetails({
    provider: IntegrationProviderEnum.Jira,
    scope: IntegrationScopeEnum.Workspace,
  });
  const { file } = useFile({ fileId });

  const [selectedFilters, { toggle }] = useListActions({
    selectedItems: [] as JiraIssueType[],
    keyExtractor: (val) => val.id!,
  });

  const clearSearch = useCallback(() => {
    setSearch('');
    setSearchWithoutDebounce('');
  }, [setSearch, setSearchWithoutDebounce]);

  const isItemSelected = useCallback(
    (jiraIssueId: any) => selectedFilters.some((item) => item.id === jiraIssueId),
    [selectedFilters],
  );

  const jiraStatusesConfig = useMemo(
    () =>
      integrationProvider?.integrationDetail?.settings?.jiraSettings?.projectStatusesConfigs?.flatMap(
        (config) => config.statuses,
      ),
    [integrationProvider?.integrationDetail?.settings?.jiraSettings?.projectStatusesConfigs],
  );

  const onCreate = useCallback(async () => {
    const actionItems: CreateActionItemInput[] = selectedFilters.map((currJiraIssue) => {
      const statusId = jiraStatusesConfig?.find((config) => config?.id === currJiraIssue.statusId)
        ?.lyearnStatusId;

      return {
        title: currJiraIssue.summary || '',
        dueDate: currJiraIssue.dueDate,
        statusId,
        assignedToId: currJiraIssue.assigneeInfo?.user?.id,
        entity:
          entityId && entity
            ? {
                entityId,
                entityType: entity,
              }
            : undefined,
        fileId: file?.systemGenerated ? undefined : fileId,
        integrationConfig: {
          providerId: provider.id,
          externalEntityType: ActionItemExternalEntityTypeEnum.JiraIssue,
          externalId: currJiraIssue.id,
        },
        metadata: {
          displayName: currJiraIssue.key,
          status: currJiraIssue.status,
          statusId: currJiraIssue.statusId,
          assigneeInfo: { emailAddress: currJiraIssue.assigneeInfo?.emailAddress },
          priorityInfo: currJiraIssue.priorityInfo,
        },
      };
    });

    if (!actionItems.length) return;

    setIsSubmitting(true);
    const data = await createActionItems({
      data: {
        actionItems,
        taskCreationConfig: {
          taskCreationType: TaskCreationTypeEnum.Manual,
        },
      },
    });

    setIsSubmitting(false);

    if (data.error) {
      showErrorSnackbar({ message: t('Jira:Tasks.FailedToAddTask') });

      return;
    }

    if (data.data?.createActionItems.actionItems) {
      onClose();
      onSubmit();
    }
  }, [
    selectedFilters,
    createActionItems,
    jiraStatusesConfig,
    entityId,
    entity,
    file?.systemGenerated,
    fileId,
    provider.id,
    showErrorSnackbar,
    t,
    onClose,
    onSubmit,
  ]);

  return {
    state: {
      jiraIssues: data,
      isSubmitting,
      search,
      debounceSearchStr,
      isLoading,
      isFetching,
      error,
      fetchMore,
      isFetchingMore,
      totalCount,
      selectedFiltersCount: selectedFilters.length,
    },
    methods: {
      toggle,
      onSubmit: onCreate,
      setSearch,
      clearSearch,
      isItemSelected,
    },
  };
}
